  import React, { useEffect, useState, useCallback } from 'react';
  import { Box, Chip, Grid, Stack, Typography } from '@mui/material';
  import { DataGrid } from '@mui/x-data-grid';
  import MainCard from 'ui-component/cards/MainCard';
  import LoadingBackdrop from 'ui-component/full-screen-loadder/LoadingBackdrop';
  import { get } from "../../axios/apiService";
  import SearchBar from 'ui-component/SearchBar/SearchBar';
  import ActionButtons from 'ui-component/actionButton/ActionButtons';
  import dayjs from 'dayjs';
  import TicketDetailsDialog from './TicketDetailsDialog';
  import { Edit, Visibility } from '@mui/icons-material';
  import { usePermissions } from 'hooks/usePermissions';
  import { MODULE_KEYS } from 'store/constant';

  const SupportTickets = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [tickets, setTickets] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [sortModel, setSortModel] = useState([{ field: 'createdDate', sort: 'desc' }]);
    const [search, setSearch] = useState('');
    const [selectedTicket, setSelectedTicket] = useState(null);
    const { hasWritePermission } = usePermissions();
    const canEdit = hasWritePermission(MODULE_KEYS.Support);

    const isTicketOpen = selectedTicket?.status === 1;

    const fetchTickets = useCallback(async () => {
      setIsLoading(true);
      try {
        const sortField = sortModel[0]?.field || 'createdDate';
        const sortOrder = sortModel[0]?.sort === 'asc' ? 'Ascending' : 'Descending';

        const sortQuery = `&$orderby=${sortField}&$orderbydirection=${sortOrder}`;
        const searchQuery = search ? `&$filter=Subject~LIKE~${search} OR FromEmail~LIKE~${search}` : '';

        const response = await get(
          `Ticket?$skip=${paginationModel.page * paginationModel.pageSize}&$top=${paginationModel.pageSize}${searchQuery}${sortQuery}`
        );

        if (!response?.values) {
          setTickets([]);
          setRowCount(0);
          return;
        }

        setRowCount(response.totalCount);
        setTickets(response.values);
      } catch (err) {
        console.error('Failed to load tickets', err);
      } finally {
        setIsLoading(false);
      }
    }, [paginationModel, sortModel, search]);

    const handleTicketUpdated = () => {
      fetchTickets(); 
      setSelectedTicket(null)
    };

    useEffect(() => {
      fetchTickets();
    }, [fetchTickets]);

    const handleSearchChange = (event) => {
      setSearch(event.target.value);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    };

    const handleSortModelChange = (model) => {
      setSortModel(model);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    };

    const columns = [
      {
        field: 'subject',
        headerName: 'Subject',
        flex: 1,
      },
      {
        field: 'calculatorName',
        headerName: 'Calculator',
        flex: 1,
        renderCell: (params) => {
          try {
            if (!params.row.metadataJson) return '-';
    
            const metadata = JSON.parse(params.row.metadataJson);
            return metadata?.calculator?.name || '-';
          } catch (err) {
            return '-';
          }
        }
      },
      {
        field: "fromEmail",
        headerName: "Email",
        flex: 1
      },
      {
        field: 'createdDate',
        headerName: 'Created',
        width: 160,
        renderCell: (params) =>
          params.value ? dayjs(params.value).format('DD-MMM-YYYY') : '-'
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        renderCell: (params) => (
          <Chip
            label={params.value === 1 ? 'Open' : 'Closed'}
            color={params.value === 1 ? 'success' : 'error'}
            size="small"
          />
        )
      },
      {
        field: 'action',
        headerName: 'Action',
        width: 100,
        sortable: false,
        renderCell: (params) => (
          <ActionButtons
          actions={[
            {
              Tooltip: "Edit",
              icon: canEdit ? <Edit/> : <Visibility />,
              color: "primary",
              onClick: () => setSelectedTicket(params.row)
            }
          ]}
          />
        )
      }
    ];

    return (
      <Box>
        <MainCard title="Support Tickets">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SearchBar
                value={search}
                onChange={handleSearchChange}
                placeholder="Search by subject"
              />
            </Grid>

            <Grid item xs={12}>
              <DataGrid
                rows={tickets}
                columns={columns}
                getRowId={(row) => row.uid}
                disableSelectionOnClick
                autoHeight
                paginationMode="server"
                sortingMode="server"
                rowCount={rowCount}
                pageSizeOptions={[10]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
              />
            </Grid>
          </Grid>
        </MainCard>

        {selectedTicket && (
          <TicketDetailsDialog
            open={!!selectedTicket}
            ticket={selectedTicket}
            onClose={() => setSelectedTicket(null)}
            isTicketOpen = {isTicketOpen}
            onStatusChanged={handleTicketUpdated}
            canEdit={canEdit}
          />
        )}
        <LoadingBackdrop open={isLoading} />
      </Box>
    );
  };

  export default SupportTickets;
